/**
 * LotException.js
 * Centralized registry for partner-specific business rules, redirects, and overrides.
 * 
 * STRUCTURE:
 * 1. PRIORITY LOCAL OVERRIDES (Hardcoded rules that must precede external JSON)
 * 2. EXTERNAL DYNAMIC RULES (Rules loaded from CDN/JSON)
 * 3. PARTNER REDIRECTS & LEGACY SUPPORT (Redirects, brand overrides, and mappings)
 * 4. DD EXCEPTION ENGINE (Technical specification suppression)
 */

export async function lotException(query, externalRules = null) {
  const prefix = query.slice(0, 4);

  // ==========================================================================
  // 1. PRIORITY LOCAL OVERRIDES (Highest Precedence)
  // ==========================================================================

  // -- ALLIED HOME / SIMMONS --
  if (prefix === "0103") {
    if (["0103L20F3034", "0103L20FN3253"].includes(query)) return ["brandName", " "];
    if (query === "01031020954") return ["brandName", "Weatherproof Vintage Home"];
    if (["0103L20FN1020", "0103L20FN1860", "0103L20FN2064"].includes(query)) return ["brandName", "ALLIED HOME"];
    if (query === "01030129150") return ["brandName", "SIMMONS FOR COSTCO"];
  }

  // -- PRIVACY & CERTS --
  if (prefix === "0310") return ["origin", true]; // Eddie Bauer Privacy Mask
  if (prefix === "0208") return ["aafa", true];   // AAFA Certification

  // -- LOLE --
  if (prefix === "1601" && ["1601L23FN2665", "1601L24FN0515", "1601L24FN0516"].includes(query)) {
    return ["brandName", "LOLE"];
  }

  // -- BEAMS --
  if (prefix === "0205") {
    if (["0205L21F157", "0205L20F1054"].includes(query)) return ["brandName", "PILGRIM SURF + SUPPLY"];
    if (query === "0205L21FN1507") return ["brandName", "B:MING by BEAMS"];
    if (query === "0205L25F1808") return ["brandName", "BEAMS PLUS"];
  }

  // -- MOUNTAIN WORKS --
  if (prefix === "1308") {
    const mtWorks = ["1308L22FN665", "1308L22FN666", "1308L22FN1652", "1308L22FN590", "1308L22F7161"];
    if (mtWorks.includes(query)) return ["brandName", "MOUNTAIN WORKS", "1323", false];
  }

  // -- ATOMIC --
  if (prefix === "0115") {
    const atomic = ["01150321125", "01150218216", "0115YL170220", "0115L16FN2576", "0115L17FN0219", "0115YL170325", "0115YL170326", "0115YL170327", "0115L17FN0218", "0115YL170115", "0115YL170311", "0115L17F273", "0115L17FN0231", "01150319527", "01150122361"];
    if (atomic.includes(query)) return ["brandName", "ATOMIC"];
  }

  // -- ADAM ET ROPE / PXG --
  if (prefix === "1605") {
    if (["1605L20F1359", "1605L20F1360"].includes(query)) return ["brandName", "PXG"];
    const adam = ["1605L19F2555", "1605L21F1608", "1605L20FN5089", "1605L22F7244", "1605L24F7101", "1605L25F1851"];
    if (adam.includes(query)) return ["brandName", "ADAM ET ROPE"];
  }

  // -- ALTON --
  if (prefix === "0127" && ["0127L22FN001", "0127L22FN1288"].includes(query)) return ["brandName", "ALTON"];

  // -- ALLIED / BROOKS BROTHERS --
  if (["1012L25F7025", "1012L25FN3441"].includes(query)) return ["brandName", "ALLIED Feather + Down"];
  if (["1012L25FN4590", "1012L25FN5891"].includes(query)) return ["brandName", "Brooks Brothers"];


  // ==========================================================================
  // 2. EXTERNAL DYNAMIC RULES (CDN)
  // ==========================================================================
  
  if (externalRules) {
    const legend = externalRules.legend;
    
    // Exact Match (e.g. 1012L25F7025)
    if (externalRules.full && externalRules.full[query]) {
      const [type, val] = externalRules.full[query];
      const result = [legend[type], val];
      console.log(`%c[EXTERNAL EXCEPTION] Full Match: ${query} ->`, "color: #007bff; font-weight: bold;", result);
      return result;
    }
    
    // Prefix Match (e.g. 1012)
    if (externalRules.prefix && externalRules.prefix[prefix]) {
      const [type, val] = externalRules.prefix[prefix];
      const result = [legend[type], val];
      console.log(`%c[EXTERNAL EXCEPTION] Prefix Match: ${prefix} ->`, "color: #17a2b8; font-weight: bold;", result);
      return result;
    }
  }


  // ==========================================================================
  // 3. PARTNER REDIRECTS & LEGACY SUPPORT
  // ==========================================================================

  // -- BESPOKE HTML REDIRECTS --
  if (prefix === "0502") {
    if (query === "0502L22F020") return ["redirect", "L22F020.html"];
    if (query === "0502L21F2620") return ["redirect", "L21F2620.html"];
  }

  // -- SALOMON / MAPPING --
  if (prefix === "1914") {
    if (query === "19141118411") return ["brandName", " "];
    if (query === "19140722536") return ["redirect", "0722536.html"];
    if (query === "1914L19F2598") return ["redirect", "salomon.html"];
  }

  // -- PREFIX 1107 (Legacy Support) --
  if (prefix === "1107") {
    const mapping = {
      "1107L23F0429": "L23F0429", "1107L23F0429-1": "L23F0429.L23F0235",
      "1107L23F0235": "L23F0235", "1107L23F0235-1": "L23F0235.L23F0429",
      "1107L23F0359": "L23F0359", "1107L22F607": "L22F607",
      "1107L23F0444": "L23F0444", "1107L23F7144": "L23F7144", "11070722544": "0722544"
    };
    if (mapping[query]) return ["display_lot", mapping[query]];
  }

  // -- PARTNER BRAND OVERRIDES --
  const overrides = {
    "0310L21F1378": ["brandName", "CJ Basquiat • Eddie Bauer"],
    "2201L17F312":  ["brandName", "Under Armour"],
    "1809L23FN1603": ["brandName", "VARG"],
    "1912L21FN366":  ["brandName", "SLY"],
    "0121L20FN1625": ["brandName", "AZUL"],
    "2118L0723754":  ["brandName", "UNITED ARROWS & SONS"],
    "2118L26F7392":  ["brandName", "D.O UNITED ARROWS BY DAISUKE OBANA"],
    "1327L21F1199":  ["brandName", "ALLIED FEATHER + DOWN"],
    "1327L21F1197":  ["brandName", "ALLIED FEATHER + DOWN"],
    "1605L26F2139" :  ["brandName", "ADAM ET ROPE"]
  };
  if (overrides[query]) return overrides[query];

  // -- PARTNER REDIRECT REGISTRY --
  const redirects = {
    "L24FN0954":    ["redirect", "?lot=1124L24FN0954"],
    "16160315375":  ["redirect", "?lot=04040315375"],
    "1806L19F2555": ["redirect", "?lot=1828L19F2555"],
    "15020417999":  ["redirect", "?lot=15020417999R"],
    "2101L22F2480": ["redirect", "?lot=2101L22F2480-1A"],
    "2505L0923776": ["redirect", "?lot=25050923776"],
    "1315L23FN5222":["redirect", "?lot=1301L23FN5222"],
    "1315L23FN4438":["redirect", "?lot=1301L23FN4438"]
  };
  if (redirects[query]) return redirects[query];

  // -- LEGACY SHORTCUTS --
  const shortcuts = {
    "L23F0429 L23F0235": "?lot=1107L23F0429-1", "L23F0429.L23F0235": "?lot=1107L23F0429-1",
    "L23F0235 L23F0429": "?lot=1107L23F0235-1", "L23F0235.L23F0429": "?lot=1107L23F0235-1",
    "L23F0235": "?lot=1107L23F0235", "L23F0359": "?lot=1107L23F0359",
    "L22F607":  "?lot=1107L22F607",  "L23F0444": "?lot=1107L23F0444",
    "L23F7144": "?lot=1107L23F7144", "0722544":  "?lot=11070722544"
  };
  if (shortcuts[query]) return ["redirect", shortcuts[query]];

  // -- SKOLMAR (0509) --
  if (prefix === "0509") {
    const skolmar = ["0509L18FN2936", "0509L19FN3589", "0509L20FN2895", "0509L22FN2203", "0509L22FN3923", "0509L22FN4935"];
    if (skolmar.includes(query)) return ["brandName", "SKOLMAR 32 AS"];
  }

  // -- BACK COUNTRY (0215 / 1918 swap) --
  if (prefix === "0215") {
    const bc = ["0215L25FN5100","0215L25F3155","0215L23FN7344","0215L24F7035","02150224873","02151223840","02150124864","0215L24F1408","0215L24F1406","0215L24F1407","0215L24F2675","0215L24F2610","0215L25F0271","0215L25F2342"];
    if (bc.includes(query)) return ["redirect", "?lot=1918" + query.slice(4)];
  }
  if (prefix === "1918") {
    const bcLots = ["1918L25FN5100","1918L25F3155","1918L23FN7344","1918L24F7035","19180224873","19181223840","19180124864","1918L24F1408","1918L24F1406","1918L24F1407","1918L24F2675","1918L24F2610","1918L25F0271","1918L25F2342"];
    if (bcLots.includes(query)) return ["brandName", "Back Country"];
  }

  return prefix;
}

/**
 * ddException Engine
 * Technical specification suppression and partner-specific data cleaning.
 */
export async function ddException(docData) {
  const { prefix, verified_fp, land_fowl, quill, down_type, origin } = docData;
  const response = { vfp: false, land_fowl: false, quill: false, origin_exception: false };

  // Fill Power suppression
  if (verified_fp === "/" || verified_fp === "0") response.vfp = true;

  // Zero-value suppression (Prefix 0404)
  if (prefix === "0404") {
    if (land_fowl === "0.0") response.land_fowl = true;
    if (quill === "0.0")     response.quill = true;
    
    // Regional masking for White Duck Down
    if (down_type === "White Duck" && origin && !origin.includes("Europe")) {
        response.origin_exception = true;
    }
  }

  return response;
}
